import React from 'react';
import cx from 'classnames';

interface TabPanelProps {
    /**
     * Any classes to add to the <div> that contains the labels
     */
    addClasses?: string;
    /**
     * Array of panels to render {@link TabPanelConfig}
     */
    panels: TabPanelConfig[];
    /**
     * Key that defines the currently active tab
     */
    activeTabKey: string;
    /**
     * Method for setting the activeTabKey
     */
    setActiveTabKey: (nextActiveTabKey: string) => void;
    /**
     * When true, the "overflow-hidden" class is added to the container
     *
     * @defaultValue true
     */
    overflowHidden?: boolean;
}

/**
 * Renders a tabbed layout, consisting of a list of panels. When a panel's `content` is visible, its
 * `label` is styled to show that it is currently active. State management is left to the parent
 * component and controlled by props for `activeTabKey` and `setActiveTabKey`.
 * @param props {@link TabPanelProps}
 */
export const TabPanel = ({
    activeTabKey,
    addClasses,
    panels,
    setActiveTabKey,
    overflowHidden = true,
}: TabPanelProps): JSX.Element => (
    <div className={cx({ 'overflow-hidden': overflowHidden })}>
        <div
            className={cx(
                addClasses,
                'flex border-solid border-b border-tertiary-200',
            )}
        >
            {panels.map(({ label, key }: TabPanelConfig): JSX.Element => {
                const isActive = key === activeTabKey;

                return (
                    <div
                        key={`${key}.label`}
                        className="mx-4 flex flex-col justify-between"
                    >
                        <button
                            type="button"
                            onClick={(): void => {
                                setActiveTabKey(key);
                            }}
                            className={cx(
                                'px-4 py-2 respond-to-mouse text-sm font-semibold tracking-wider',
                                {
                                    'text-tertiary-400': !isActive,
                                },
                            )}
                        >
                            {label}
                        </button>
                        <div
                            className={cx({
                                'border-solid border-b-4 border-primary-500 rounded-full':
                                    isActive,
                            })}
                        />
                    </div>
                );
            })}
        </div>

        {panels.map(({ contents, key }: TabPanelConfig): JSX.Element => {
            const isActive = key === activeTabKey;

            return (
                <div
                    key={`${key}.contents`}
                    className={cx('mt-4', {
                        hidden: !isActive,
                    })}
                >
                    {contents}
                </div>
            );
        })}
    </div>
);
